/**
 * sessionRestore.js — предложение вернуться туда, где вкладку убил браузер.
 *
 * Появляется только после настоящей выгрузки: программа уходила в фон и
 * стартовала заново, не вернувшись. При обычном запуске молчит.
 *
 * Сам разбор «что можно предложить» живёт в core/sessionState.js и покрыт
 * тестами. Здесь только показ и действие.
 */

import { log } from '../utils/logger.js';
import { planSessionRestore, getSession, clearSession, noteViewState } from '../core/sessionState.js';
import { wasDiscardedAtStart } from './recoveryReport.js';
import { mountNotice, SLOT } from './notifyHost.js';   // [9.0.2] общий столбец уведомлений

function _esc(s) { return String(s).replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c])); }

/** Лежит ли исследование в базе. Спрашиваем лениво: база тяжёлая. */
async function _studyInDb(studyId) {
  if (!studyId) return false;
  try {
    const m = await import('../storage/dicomStore.js');
    const st = await m.getStudy?.(studyId);
    return !!st;
  } catch (e) { log.debug('[session] база недоступна:', e.message); return false; }
}

async function _reopen(studyId) {
  const m = await import('../storage/dicomStore.js');
  const files = await m.loadStudyFiles(studyId);
  if (!files?.length) throw new Error('в базе нет файлов этого исследования');
  const api = globalThis.MikhmelViewer;
  if (typeof api?.handleFiles === 'function') { await api.handleFiles(files); return true; }
  if (typeof api?.loadDICOMFiles === 'function') { await api.loadDICOMFiles(files); return true; }
  throw new Error('некому передать файлы — обработчик загрузки не зарегистрирован');
}

function _card({ title, detail, actionLabel, onAction }) {
  try {
    document.getElementById('session-restore')?.remove();
    const box = document.createElement('div');
    box.id = 'session-restore';
    box.style.cssText = 'background:#12243a;color:#e6eefb;border:1px solid #3a6ea5;border-radius:12px;'
      + 'padding:12px 14px;font:13px/1.45 system-ui,sans-serif;'
      + 'box-shadow:0 8px 28px rgba(0,0,0,.5)';
    box.innerHTML = `
      <div data-notice-title style="font-weight:700;font-size:14px;margin-bottom:4px">${_esc(title)}</div>
      <div style="opacity:.9">${_esc(detail)}</div>
      <div style="display:flex;gap:8px;margin-top:10px">
        ${actionLabel ? `<button id="sr-go" style="flex:1;background:#37a;color:#fff;border:0;border-radius:8px;padding:10px;font-weight:700;font-size:14px;cursor:pointer">${_esc(actionLabel)}</button>` : ''}
        <button id="sr-x" style="background:transparent;border:1px solid #456;color:#bcd;border-radius:8px;padding:10px 14px;cursor:pointer">Закрыть</button>
      </div>`;
    mountNotice(box, SLOT.ACTION);
    box.querySelector('#sr-x').onclick = () => box.remove();
    const go = box.querySelector('#sr-go');
    if (go && onAction) {
      go.onclick = async () => {
        go.disabled = true; go.textContent = 'Открываю…';
        try { await onAction(); box.remove(); }
        catch (e) {
          go.disabled = false; go.textContent = 'Не вышло — повторить';
          log.warn('[session] восстановление не удалось:', e.message);
        }
      };
    }
    return box;
  } catch (e) { log.warn('[session] показ карточки:', e.message); return null; }
}

/**
 * Применить сохранённый вид. Зовётся и после自 восстановления, и после того,
 * как человек сам открыл ту же серию — во втором случае это всё, что мы можем.
 */
export function applySavedView() {
  const s = getSession();
  if (!s?.view) return false;
  try {
    const api = globalThis.MikhmelViewer;
    if (typeof api?.restoreViewState === 'function') { api.restoreViewState(s.view); return true; }
  } catch (e) { log.debug('[session] применение вида:', e.message); }
  return false;
}

/**
 * [10.0.2] Вернуть последнее исследование по требованию.
 *
 * Плавающая кнопка со свёрнутой карточкой появляется только после того, как
 * браузер выгрузил вкладку. Это правильно — не мозолить глаза, — но означает,
 * что найти её, когда она нужна, нельзя: она либо есть, либо повода нет.
 * Поэтому то же действие живёт постоянно в ящике «Дополнительно».
 *
 * @returns {Promise<boolean>} удалось ли открыть
 */
export async function reopenLastStudy() {
  const s = getSession();
  const studyId = s?.studyId;
  if (!studyId) {
    _say('Нет сохранённого исследования — возвращать нечего');
    return false;
  }
  if (!(await _studyInDb(studyId))) {
    _say('Снимки этого исследования не сохранены в базе — откройте файлы заново');
    return false;
  }
  try {
    await _reopen(studyId);
    setTimeout(applySavedView, 1500);
    return true;
  } catch (e) {
    log.warn('[session] возврат по кнопке:', e.message);
    _say(`Не удалось вернуть: ${e.message}`);
    return false;
  }
}

/** Сообщить человеку, не таща сюда весь модуль подсказок. */
function _say(text) {
  try {
    import('../utils/helpers.js').then(m => m.showToast?.(text, 'warning', 4000))
      .catch(() => log.info(`[session] ${text}`));
  } catch (e) { log.info(`[session] ${text}`); }
}

export async function initSessionRestore() {
  // Постоянная кнопка в ящике «Дополнительно» — привязывается всегда,
  // независимо от того, была выгрузка вкладки или нет.
  try {
    document.getElementById('btn-restore-volume')
      ?.addEventListener('click', () => { reopenLastStudy(); });
  } catch (e) { log.debug('[session] кнопка возврата:', e.message); }

  try {
    const session = getSession();
    const wasDiscarded = wasDiscardedAtStart();
    // Спрашиваем базу только если без неё решение не принять.
    const studyInDb = (wasDiscarded && session?.studyId) ? await _studyInDb(session.studyId) : false;

    const plan = planSessionRestore({ session, wasDiscarded, studyInDb, now: Date.now() });
    if (plan.action === 'none') return null;

    log.info(`[session] после выгрузки вкладки: ${plan.action}`);

    if (plan.action === 'reopen') {
      return _card({
        title: plan.title, detail: plan.detail, actionLabel: 'Открыть заново',
        onAction: async () => {
          await _reopen(plan.studyId);
          if (plan.canRestoreView) setTimeout(applySavedView, 1500);
        },
      });
    }
    // view-only: обещать возврат снимков нельзя, и мы этого не делаем
    return _card({ title: plan.title, detail: plan.detail, actionLabel: null, onAction: null });
  } catch (e) { log.warn('[session]', e.message); return null; }
}

export { noteViewState, clearSession };
export default { initSessionRestore, applySavedView, reopenLastStudy };
